import {
  ProcessConfig,
  ProcessedImage,
  FileInfo,
  ImageDimensions,
  ImagePosition,
  ProcessResult,
  ErrorCode,
  ProcessError,
  CONSTANTS
} from '../types';

/**
 * 核心图片处理服务类
 * 负责将图片处理为指定尺寸的白底图片
 */
export class ImageProcessor {
  private config: ProcessConfig;

  constructor(config?: Partial<ProcessConfig>) {
    this.config = {
      canvasWidth: config?.canvasWidth ?? CONSTANTS.DEFAULT_CANVAS_WIDTH,
      canvasHeight: config?.canvasHeight ?? CONSTANTS.DEFAULT_CANVAS_HEIGHT,
      backgroundColor: config?.backgroundColor ?? CONSTANTS.DEFAULT_BACKGROUND_COLOR,
      quality: config?.quality ?? CONSTANTS.DEFAULT_QUALITY
    };
  }

  /**
   * 更新处理配置
   */
  updateConfig(config: Partial<ProcessConfig>): void {
    this.config = { ...this.config, ...config };
  }

  /**
   * 处理单张图片
   */
  async processImage(fileInfo: FileInfo, onProgress?: (progress: number) => void): Promise<ProcessResult> {
    const startTime = performance.now();

    try {
      // 1. 验证文件
      onProgress?.(10);
      const validation = this.validateFile(fileInfo.file);
      if (!validation.valid) {
        throw validation.error;
      }

      // 2. 加载图片
      onProgress?.(30);
      const imageElement = await this.loadImage(fileInfo.file);

      // 3. 计算尺寸和位置
      onProgress?.(50);
      const originalDimensions = this.getImageDimensions(imageElement);
      const position = this.calculateImagePosition(originalDimensions);

      // 4. 创建Canvas并绘制
      onProgress?.(70);
      const canvas = this.createCanvas();
      const ctx = this.getCanvasContext(canvas);

      // 绘制背景
      this.drawBackground(ctx);

      // 绘制图片
      this.drawImage(ctx, imageElement, position);

      // 5. 导出图片
      onProgress?.(90);
      const processedBlob = await this.canvasToBlob(canvas, fileInfo.file.type);
      const processedUrl = URL.createObjectURL(processedBlob);

      // 确定输出格式以生成正确的文件名
      const outputFormat = (fileInfo.file.type === 'image/png' || fileInfo.file.type === 'image/webp')
        ? 'image/png'
        : 'image/jpeg';

      // 6. 生成结果
      onProgress?.(100);
      const processingTime = performance.now() - startTime;

      const processedImage: ProcessedImage = {
        id: fileInfo.id,
        originalFile: fileInfo,
        processedBlob,
        processedUrl,
        originalDimensions,
        processedDimensions: {
          width: this.config.canvasWidth,
          height: this.config.canvasHeight,
          aspectRatio: this.config.canvasWidth / this.config.canvasHeight
        },
        position,
        processingTime,
        processedAt: Date.now(),
        downloadName: this.generateDownloadName(fileInfo.name, outputFormat)
      };

      return {
        success: true,
        data: processedImage
      };

    } catch (error) {
      return {
        success: false,
        error: this.createProcessError(error)
      };
    }
  }

  /**
   * 验证文件格式和大小
   */
  private validateFile(file: File): { valid: boolean; error?: ProcessError } {
    // 检查文件大小
    if (file.size > CONSTANTS.MAX_FILE_SIZE) {
      return {
        valid: false,
        error: {
          code: ErrorCode.FILE_TOO_LARGE,
          message: `文件大小超出限制，最大支持 ${this.formatFileSize(CONSTANTS.MAX_FILE_SIZE)}`,
          recoverable: false,
          timestamp: Date.now()
        }
      };
    }

    // 检查文件格式
    if (!CONSTANTS.SUPPORTED_FORMATS.includes(file.type)) {
      return {
        valid: false,
        error: {
          code: ErrorCode.UNSUPPORTED_FORMAT,
          message: `不支持的文件格式，支持格式：${CONSTANTS.SUPPORTED_FORMATS.join(', ')}`,
          recoverable: false,
          timestamp: Date.now()
        }
      };
    }

    return { valid: true };
  }

  /**
   * 加载图片文件为HTMLImageElement
   */
  private loadImage(file: File): Promise<HTMLImageElement> {
    return new Promise((resolve, reject) => {
      const img = new Image();
      const url = URL.createObjectURL(file);

      img.onload = () => {
        URL.revokeObjectURL(url);
        resolve(img);
      };

      img.onerror = () => {
        URL.revokeObjectURL(url);
        reject(new Error('图片加载失败'));
      };

      img.src = url;
    });
  }

  /**
   * 获取图片尺寸信息
   */
  private getImageDimensions(img: HTMLImageElement): ImageDimensions {
    return {
      width: img.naturalWidth,
      height: img.naturalHeight,
      aspectRatio: img.naturalWidth / img.naturalHeight
    };
  }

  /**
   * 计算图片在画布上的位置和尺寸
   * 核心算法：保持原图比例，居中放置，不超出画布
   */
  private calculateImagePosition(originalDimensions: ImageDimensions): ImagePosition {
    const { width: originalWidth, height: originalHeight } = originalDimensions;
    const { canvasWidth, canvasHeight } = this.config;

    // 计算缩放比例，取较小值确保图片完全显示在画布内
    const scaleX = canvasWidth / originalWidth;
    const scaleY = canvasHeight / originalHeight;
    const scale = Math.min(scaleX, scaleY, 1); // 不放大，只缩小

    // 计算缩放后的尺寸
    const scaledWidth = originalWidth * scale;
    const scaledHeight = originalHeight * scale;

    // 计算居中位置
    const x = (canvasWidth - scaledWidth) / 2;
    const y = (canvasHeight - scaledHeight) / 2;

    return {
      x,
      y,
      width: scaledWidth,
      height: scaledHeight,
      scale
    };
  }

  /**
   * 创建Canvas元素
   */
  private createCanvas(): HTMLCanvasElement {
    const canvas = document.createElement('canvas');
    canvas.width = this.config.canvasWidth;
    canvas.height = this.config.canvasHeight;
    return canvas;
  }

  /**
   * 获取Canvas 2D上下文
   */
  private getCanvasContext(canvas: HTMLCanvasElement): CanvasRenderingContext2D {
    const ctx = canvas.getContext('2d');
    if (!ctx) {
      throw new Error('无法获取Canvas 2D上下文');
    }
    return ctx;
  }

  /**
   * 绘制背景色
   */
  private drawBackground(ctx: CanvasRenderingContext2D): void {
    ctx.fillStyle = this.config.backgroundColor;
    ctx.fillRect(0, 0, this.config.canvasWidth, this.config.canvasHeight);
  }

  /**
   * 在Canvas上绘制图片
   */
  private drawImage(ctx: CanvasRenderingContext2D, img: HTMLImageElement, position: ImagePosition): void {
    // 启用图片平滑处理
    ctx.imageSmoothingEnabled = true;
    ctx.imageSmoothingQuality = 'high';

    // 绘制图片
    ctx.drawImage(
      img,
      0, 0, img.naturalWidth, img.naturalHeight, // 源图片区域
      position.x, position.y, position.width, position.height // 目标区域
    );
  }

  /**
   * 将Canvas转换为Blob
   */
  private canvasToBlob(canvas: HTMLCanvasElement, originalFileType?: string): Promise<Blob> {
    return new Promise((resolve, reject) => {
      // 根据原始文件类型选择最佳输出格式
      let outputFormat = 'image/jpeg';

      if (originalFileType === 'image/png' || originalFileType === 'image/webp') {
        // PNG和WebP原始文件使用PNG输出，无损压缩
        outputFormat = 'image/png';

        // PNG格式不需要质量参数
        canvas.toBlob(
          (blob) => {
            if (blob) {
              resolve(blob);
            } else {
              reject(new Error('Canvas转换失败'));
            }
          },
          outputFormat
        );
      } else {
        // JPEG格式使用质量参数
        canvas.toBlob(
          (blob) => {
            if (blob) {
              resolve(blob);
            } else {
              reject(new Error('Canvas转换失败'));
            }
          },
          outputFormat,
          this.config.quality
        );
      }
    });
  }

  /**
   * 生成下载文件名
   */
  private generateDownloadName(originalName: string, outputFormat: string): string {
    const name = originalName.replace(/\.[^/.]+$/, ''); // 去掉扩展名
    const timestamp = new Date().toISOString().slice(0, 19).replace(/[:-]/g, '');

    // 根据输出格式选择扩展名
    const extension = outputFormat === 'image/png' ? 'png' : 'jpg';

    return `${name}_processed_${timestamp}.${extension}`;
  }

  /**
   * 创建处理错误对象
   */
  private createProcessError(error: unknown): ProcessError {
    if (error instanceof Error) {
      return {
        code: ErrorCode.PROCESSING_ERROR,
        message: error.message,
        recoverable: true,
        timestamp: Date.now(),
        details: error.stack
      };
    }

    return {
      code: ErrorCode.UNKNOWN_ERROR,
      message: '未知错误',
      recoverable: false,
      timestamp: Date.now(),
      details: error
    };
  }

  /**
   * 格式化文件大小
   */
  private formatFileSize(bytes: number): string {
    const units = ['B', 'KB', 'MB', 'GB'];
    let size = bytes;
    let unitIndex = 0;

    while (size >= 1024 && unitIndex < units.length - 1) {
      size /= 1024;
      unitIndex++;
    }

    return `${size.toFixed(1)} ${units[unitIndex]}`;
  }

  /**
   * 清理资源
   */
  cleanup(): void {
    // 清理可能的内存泄漏
    // 在批量处理时调用
  }
}

/**
 * 默认图片处理器实例
 */
export const defaultImageProcessor = new ImageProcessor();